/* eslint-disable @typescript-eslint/no-explicit-any */
import { sortBy } from 'es-toolkit/compat';

import { clowncar, core, dashboard } from 'types';
import { toJSON, toJSONArray } from '@tock/coretypes';
import ClowncarAppConstants from 'constants/ClowncarAppConstants';
import clowncarProtos from 'utils/protos';

const { SearchAttribute } = clowncarProtos;

interface State {
  bankAccount?: dashboard.BankAccount;
  business?: core.Business;
  businesses: core.Business[];
  exportBusinesses: core.Business[];
  importBusinesses: core.Business[];
  businessGroups: core.BusinessGroup[];
  currentSearch: string;
  unlisted: boolean;
  isInitialized: boolean;
  selectedBusinessDetails?: dashboard.BusinessInternalDetails | null;
  subscriptions: core.BusinessPayment[];
  subscriptionPlans: dashboard.Subscription[];
  subscriptionPlanAddons: dashboard.SubscriptionAddon[];
  requestInProgress: boolean;
  searchAttribute?: clowncar.SearchAttribute | any;
  isSearchAttributeInitialized: boolean;
}

const initialState: State = {
  bankAccount: undefined,
  business: undefined,
  businesses: [],
  exportBusinesses: [],
  importBusinesses: [],
  businessGroups: [],
  currentSearch: '',
  unlisted: false,
  isInitialized: false,
  selectedBusinessDetails: null,
  subscriptions: [],
  subscriptionPlans: [],
  subscriptionPlanAddons: [],
  requestInProgress: false,
  searchAttribute: SearchAttribute,
  isSearchAttributeInitialized: false,
};

export default function reducer(state = initialState, action: BaseTockAction) {
  const { type, payload } = action;
  switch (type) {
    case ClowncarAppConstants.BUSINESS_TYPE_UPDATE_DONE:
    case ClowncarAppConstants.INTERNAL_BUSINESS_DETAILS_UPDATE_DONE:
    case ClowncarAppConstants.BUSINESS_SET: {
      return {
        ...state,
        business: toJSON<any>(payload),
      };
    }
    case ClowncarAppConstants.BUSINESSES_FETCH_EXPORT_ENVIRONMENT_DONE: {
      return {
        ...state,
        isInitialized: true,
        exportBusinesses: toJSONArray<core.Business>(payload),
        requestInProgress: false,
      };
    }
    case ClowncarAppConstants.BUSINESSES_FETCH_IMPORT_ENVIRONMENT_DONE: {
      return {
        ...state,
        isInitialized: true,
        importBusinesses: toJSONArray<core.Business>(payload),
        requestInProgress: false,
      };
    }
    case ClowncarAppConstants.BUSINESSES_FETCH_DONE: {
      return {
        ...state,
        isInitialized: true,
        businesses: payload,
        requestInProgress: false,
      };
    }
    case ClowncarAppConstants.BUSINESSES_SEARCH_SET: {
      return {
        ...state,
        currentSearch: payload,
      };
    }
    case ClowncarAppConstants.BUSINESSES_SET_FILTER_GUEST_MARKETING: {
      return {
        ...state,
        unlisted: payload,
      };
    }
    case ClowncarAppConstants.BUSINESS_FETCH_STARTING:
    case ClowncarAppConstants.BUSINESSES_FETCH_STARTING:
    case ClowncarAppConstants.BUSINESSES_FETCH_EXPORT_ENVIRONMENT_STARTING:
    case ClowncarAppConstants.BUSINESSES_FETCH_IMPORT_ENVIRONMENT_STARTING:
    case ClowncarAppConstants.BUSINESSES_UPDATE_GUEST_MARKETING_STARTING:
    case ClowncarAppConstants.CUSTOM_TOS_UPLOAD_STARTING:
    case ClowncarAppConstants.SUBSCRIPTION_UPDATE_STARTING: {
      return {
        ...state,
        requestInProgress: true,
        selectedBusinessDetails: null,
      };
    }
    case ClowncarAppConstants.BUSINESSES_SEARCH_ATTRIBUTE_INSERT_STARTING:
    case ClowncarAppConstants.BUSINESSES_SEARCH_ATTRIBUTE_UPDATE_STARTING:
    case ClowncarAppConstants.BUSINESSES_SEARCH_ATTRIBUTE_FETCH_STARTING:
      return {
        ...state,
        requestInProgress: true,
        isSearchAttributeInitialized: false,
      };
    case ClowncarAppConstants.BUSINESS_FETCH_DONE: {
      return {
        ...state,
        requestInProgress: false,
        selectedBusinessDetails: payload,
      };
    }
    case ClowncarAppConstants.BUSINESS_FETCH_FAILED:
    case ClowncarAppConstants.BUSINESSES_FETCH_FAILED:
    case ClowncarAppConstants.BUSINESSES_FETCH_EXPORT_ENVIRONMENT_FAILED:
    case ClowncarAppConstants.BUSINESSES_FETCH_IMPORT_ENVIRONMENT_FAILED:
    case ClowncarAppConstants.BUSINESSES_SEARCH_ATTRIBUTE_INSERT_FAILED:
    case ClowncarAppConstants.BUSINESSES_SEARCH_ATTRIBUTE_UPDATE_FAILED:
    case ClowncarAppConstants.BUSINESSES_UPDATE_GUEST_MARKETING_FAILED:
    case ClowncarAppConstants.CUSTOM_TOS_UPLOAD_FAILED:
    case ClowncarAppConstants.SUBSCRIPTION_UPDATE_FAILED:
    case ClowncarAppConstants.SUBSCRIPTION_UPDATE_DONE:
    case ClowncarAppConstants.BUSINESSES_UPDATE_GUEST_MARKETING_DONE: {
      return {
        ...state,
        requestInProgress: false,
      };
    }
    case ClowncarAppConstants.SUBSCRIPTIONS_FETCH_DONE: {
      return {
        ...state,
        subscriptions: payload,
      };
    }
    case ClowncarAppConstants.BUSINESSES_SEARCH_ATTRIBUTE_INSERT_DONE:
    case ClowncarAppConstants.BUSINESSES_SEARCH_ATTRIBUTE_UPDATE_DONE:
    case ClowncarAppConstants.BUSINESSES_SEARCH_ATTRIBUTE_FETCH_DONE: {
      return {
        ...state,
        searchAttribute: toJSON(payload),
        isSearchAttributeInitialized: true,
      };
    }
    case ClowncarAppConstants.SUBSCRIPTION_PLANS_FETCH_DONE: {
      const subscriptionPlans = sortBy(
        toJSONArray<dashboard.Subscription>(payload),
        'monthlySubscriptionFeeCents',
        'desc'
      );
      return {
        ...state,
        subscriptionPlans,
      };
    }
    case ClowncarAppConstants.SUBSCRIPTION_PLAN_ADDONS_FETCH_DONE: {
      const subscriptionPlanAddons = sortBy(
        toJSONArray<dashboard.SubscriptionAddon>(payload),
        'amountCents',
        'asc'
      );
      return {
        ...state,
        subscriptionPlanAddons,
      };
    }
    case ClowncarAppConstants.BANK_ACCOUNT_FETCH_DONE: {
      return {
        ...state,
        bankAccount: payload,
      };
    }
    default:
      return state;
  }
}
